<template>
  <div class="columns is-multiline">
    <div class="column is-full">
      <div class="card">
        <header class="card-header">
          <p class="card-header-title">Πόντοι επισκεπτών</p>
        </header>
        <div class="card-content">
          <ReportDateRange
            :from.sync="from"
            :to.sync="to"
            :preset="activePreset"
            :validation-error="validationError"
            :range-in-days-label="rangeInDaysLabel"
            @preset="onRangePreset"
          />

          <div class="columns is-multiline" style="margin-top: 0.5rem">
            <div
              v-for="stat in statsCards"
              :key="stat.label"
              class="column is-one-quarter"
            >
              <div class="box has-text-centered">
                <p class="heading">{{ stat.label }}</p>
                <p class="title is-4">{{ stat.value }}</p>
              </div>
            </div>
          </div>
          <p class="help has-text-grey">
            Οι μετρήσεις αφορούν claims που δημιουργήθηκαν στο επιλεγμένο
            διάστημα. «Έγιναν profile» = ο επισκέπτης έκανε εγγραφή/σύνδεση με
            το ίδιο email μέσα στο παράθυρο.
          </p>

          <hr />
          <div class="columns is-vcentered is-multiline">
            <div class="column is-one-quarter">
              <label class="label" for="guest-claim-status">Κατάσταση</label>
              <div class="select is-fullwidth">
                <select id="guest-claim-status" v-model="status">
                  <option value="">Όλες</option>
                  <option value="active">Ενεργές</option>
                  <option value="expired">Έληξαν</option>
                  <option value="claimed">Δεσμεύτηκαν</option>
                  <option value="credited">Πιστώθηκαν</option>
                </select>
              </div>
            </div>
            <div class="column is-half">
              <label class="label" for="guest-claim-search">Αναζήτηση</label>
              <input
                id="guest-claim-search"
                v-model="search"
                class="input"
                type="search"
                placeholder="Email ή αριθμός παραγγελίας"
              />
            </div>
          </div>

          <div v-if="isLoading" class="has-text-centered">
            <span class="icon"><i class="fa fa-spinner fa-spin"/></span>
          </div>
          <div v-else-if="claims.length" class="table-container">
            <table class="table is-bordered is-striped is-fullwidth">
              <thead>
                <tr>
                  <th>Ημ/νία</th>
                  <th>Παραγγελία</th>
                  <th>Email παραγγελίας</th>
                  <th>Πόντοι</th>
                  <th>Κατάσταση</th>
                  <th>Προφίλ</th>
                  <th>Λήξη / Δέσμευση / Πίστωση</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="claim in claims" :key="claim.uuid">
                  <td>{{ formatDate(claim.created_at) }}</td>
                  <td>
                    <router-link
                      v-if="claim.order"
                      :to="{
                        name: 'orders.edit',
                        params: { uuid: claim.order.uuid },
                      }"
                    >
                      #{{ claim.order.common_id }}
                    </router-link>
                    <span v-else>—</span>
                  </td>
                  <td>{{ claim.email }}</td>
                  <td>{{ claim.points_count }}</td>
                  <td>
                    <span :class="statusClass(claim.status)">{{
                      statusLabel(claim.status)
                    }}</span>
                  </td>
                  <td>
                    <router-link
                      v-if="claim.profile && claim.profile.customer_uuid"
                      :to="{
                        name: 'customers.edit',
                        params: { uuid: claim.profile.customer_uuid },
                      }"
                    >
                      {{ claim.profile.email }}
                    </router-link>
                    <span v-else>{{
                      claim.profile ? claim.profile.email : '—'
                    }}</span>
                  </td>
                  <td>{{ formatDate(relevantDate(claim)) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="has-text-centered">
            Δεν υπάρχουν claims στο διάστημα
          </p>

          <NewPagination
            v-if="pagination.total"
            :items="claims"
            :total="pagination.total"
            :current-page="pagination.current_page"
            :pages-count="pagination.last_page"
            :items-per-page="25"
            :page-size-options="[25]"
            :is-loading="isLoading"
            @navigate="onNavigate"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import moment from 'moment';
import request from '@/utils/request';
import reportDateRange from '@/mixins/reportDateRange';
import ReportDateRange from '@/views/Dashboard/views/Stats/components/ReportDateRange';
import NewPagination from '@/views/components/PaginatedList/components/NewPagination';

const EMPTY_PAGINATION = {
  current_page: 1,
  last_page: 1,
  total: 0,
};

export default {
  components: { ReportDateRange, NewPagination },
  mixins: [reportDateRange],

  data() {
    return {
      claims: [],
      summary: {},
      pagination: { ...EMPTY_PAGINATION },
      status: '',
      search: '',
      isLoading: false,
      latestRequestId: 0,
      reportRangeAllowsToday: true,
      reportMaxRangeDays: null,
      scheduleRangeLoad: null,
      searchLoad: null,
    };
  },

  computed: {
    statsCards() {
      const stats = this.summary || {};
      return [
        { label: 'Guest παραγγελίες με πόντους', value: stats.claims || 0 },
        { label: 'Έγιναν profile', value: stats.claimed || 0 },
        {
          label: 'Ποσοστό μετατροπής',
          value: `${stats.conversion_rate || 0}%`,
        },
        { label: 'Πιστώθηκαν', value: stats.credited || 0 },
        { label: 'Έληξαν', value: stats.expired || 0 },
        { label: 'Ενεργές', value: stats.active || 0 },
        { label: 'Πόντοι δεσμευμένοι', value: stats.points_reserved || 0 },
        { label: 'Πόντοι πιστωμένοι', value: stats.points_credited || 0 },
      ];
    },
  },

  watch: {
    from() {
      this.scheduleRangeLoad();
    },
    to() {
      this.scheduleRangeLoad();
    },
    status() {
      this.loadClaims(1);
    },
    search() {
      this.searchLoad();
    },
  },

  created() {
    this.scheduleRangeLoad = debounce(() => this.loadClaims(1), 0);
    this.searchLoad = debounce(() => this.loadClaims(1), 300);
  },

  mounted() {
    this.scheduleRangeLoad();
  },

  beforeDestroy() {
    this.latestRequestId += 1;
    this.scheduleRangeLoad.cancel();
    this.searchLoad.cancel();
  },

  methods: {
    onRangePreset(preset) {
      if (preset === 'custom') {
        this.onCustomDateChange();
      } else {
        this.applyPreset(preset);
      }
    },

    onNavigate({ page }) {
      this.loadClaims(page);
    },

    statusLabel(value) {
      return (
        {
          active: 'Ενεργή',
          expired: 'Έληξε',
          claimed: 'Δεσμεύτηκε',
          credited: 'Πιστώθηκε',
        }[value] || value
      );
    },

    statusClass(value) {
      return (
        {
          active: 'tag is-light',
          expired: 'tag is-light',
          claimed: 'tag is-info',
          credited: 'tag is-success',
        }[value] || 'tag'
      );
    },

    relevantDate(claim) {
      if (claim.status === 'credited') return claim.credited_at;
      if (claim.status === 'claimed') return claim.claimed_at;
      return claim.expires_at;
    },

    formatDate(value) {
      return value
        ? moment
            .utc(value)
            .local()
            .format('DD/MM/YYYY HH:mm')
        : '—';
    },

    async loadClaims(page = 1) {
      this.latestRequestId += 1;
      const requestId = this.latestRequestId;
      if (!this.from || !this.to || this.validationError) {
        this.isLoading = false;
        this.summary = {};
        this.claims = [];
        this.pagination = { ...EMPTY_PAGINATION };
        return;
      }
      this.isLoading = true;
      try {
        const { data } = await request.get('/guest-order-claims', {
          params: {
            from: this.from,
            to: this.to,
            status: this.status || undefined,
            search: this.search.trim() || undefined,
            page,
          },
        });
        if (requestId !== this.latestRequestId) return;
        this.summary = data.summary || {};
        this.pagination = data.claims || { ...EMPTY_PAGINATION };
        this.claims = this.pagination.data || [];
      } catch (error) {
        if (requestId !== this.latestRequestId) return;
        this.summary = {};
        this.claims = [];
        this.pagination = { ...EMPTY_PAGINATION };
      } finally {
        if (requestId === this.latestRequestId) this.isLoading = false;
      }
    },
  },
};
</script>
